import {
  matchTrackerApi,
  metricsServerApi,
  streamingApi
} from '../../modules/servers/api'
import { upsertOneEvent } from '../events'
import { RawSessionEventData } from '../events/types'
import { setActiveId } from './slice'
import {
  RawSessionData,
  ScoreboardType,
  SessionChunkResponse,
  SessionChunkResponseRaw
} from './types'

export type CreateSessionRequest = Omit<
  RawSessionEventData,
  | 'id'
  | 'createdAt'
  | 'updatedAt'
  | 'ignore'
  | 'startTime'
  | 'endTime'
  | 'tags'
  | 'outcomes'
  | 'properties'
  | 'data'
  | 'parentId'
> & {
  data: Omit<RawSessionData, 'id' | 'eventID'>
}

export const sessionsApi = metricsServerApi.injectEndpoints({
  endpoints: (builder) => ({
    // Check Active Session
    getActiveSession: builder.query<RawSessionEventData, void>({
      providesTags: ['activeSession'],
      query: () => ({
        url: `api/config/activesession`,
        method: 'GET',
        withCredentials: true
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled
          if (response.data.id) {
            dispatch(setActiveId(response.data.id))
          }
        } catch (error) {
          dispatch(setActiveId(null))
        }
      }
    }),

    // Create Session
    createSession: builder.mutation<RawSessionEventData, CreateSessionRequest>({
      query: (data) => ({
        url: `api/v2/sessions`,
        method: 'POST',
        body: data,
        withCredentials: true
      }),
      invalidatesTags: ['matchState'],
      onQueryStarted: async (data, { dispatch, queryFulfilled }) => {
        try {
          const response = await queryFulfilled
          // Add session to events
          const sessionData = response.data
          dispatch(upsertOneEvent(sessionData))

          if (sessionData && sessionData.data.state === 'Started') {
            dispatch(setActiveId(sessionData.id))
          }
        } catch (error) {
          console.error('Error creating session:', error)
        }
      }
    }),

    // Update Session
    updateSession: builder.mutation<
      RawSessionEventData,
      Pick<RawSessionEventData, 'id'> &
        Partial<
          Omit<RawSessionEventData, 'id' | 'data'> & {
            data: Partial<RawSessionData>
          }
        >
    >({
      query: (data) => ({
        url: `api/v2/sessions/${data.id}`,
        method: 'PUT',
        body: data,
        withCredentials: true
      }),
      invalidatesTags: ['matchState'],
      onQueryStarted: async (data, { dispatch, queryFulfilled }) => {
        try {
          const response = await queryFulfilled

          // Add session to events
          const sessionData = response.data
          dispatch(upsertOneEvent(sessionData))

          if (sessionData && sessionData.data.state === 'Started') {
            dispatch(setActiveId(data.id))
          }

          if (sessionData && sessionData.data.state === 'Finished') {
            dispatch(setActiveId(null))
          }
        } catch (error) {
          console.error('Error updating session:', error)
        }
      }
    }),

    // Get Scoreboard
    getScoreboard: builder.query<ScoreboardType, string>({
      query: (sessionId) => ({
        url: `/sessions/${sessionId}/scoreboard`,
        method: 'GET',
        withCredentials: true
      })
    })
  })
})

export const sessionMtApi = matchTrackerApi.injectEndpoints({
  endpoints: (builder) => ({
    // Export Session to GCP
    reprocessSession: builder.mutation<
      any,
      { sessionId: string; orgId: string }
    >({
      query: ({ sessionId, orgId }) => ({
        url: `session/${sessionId}/reprocess?orgId=${orgId}`,
        method: 'POST',
        withCredentials: true
      })
    })
  })
})

export const sessionStreamingApi = streamingApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get chunk metadata for session replay
    getSessionChunks: builder.query<SessionChunkResponse, string>({
      query: (sessionId) => ({
        url: `session/${sessionId}/chunks`,
        method: 'GET',
        withCredentials: true
      }),
      transformResponse: (
        raw: SessionChunkResponseRaw
      ): SessionChunkResponse => ({
        ...raw,
        chunks: raw.chunks.map((c) => ({
          ...c,
          startTime: new Date(c.startTime),
          endTime: new Date(c.endTime)
        }))
      })
    })
  })
})

export const {
  useGetActiveSessionQuery,
  useLazyGetActiveSessionQuery,
  useCreateSessionMutation,
  useUpdateSessionMutation,
  useGetScoreboardQuery
} = sessionsApi

export const { getActiveSession, updateSession, getScoreboard } =
  sessionsApi.endpoints

export const { useGetSessionChunksQuery } = sessionStreamingApi

export const { reprocessSession } = sessionMtApi.endpoints
export const { getSessionChunks } = sessionStreamingApi.endpoints
